import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmationDialog } from '@shared/ui/components/ConfirmationDialog';
import { useState } from 'react';
import { useListCoachesQuery } from './api/coachesApi';
import {
  type CreateFlagRequest,
  type FeatureFlag,
  type FlagType,
  type TargetRole,
  useAddPersonToFlagMutation,
  useCreateFlagMutation,
  useDeleteFlagMutation,
  useGetFlagPeopleQuery,
  useListFeatureFlagsQuery,
  useRemovePersonFromFlagMutation,
  useUpdateFeatureFlagMutation,
} from './api/featureFlagsApi';
import { useListMembersQuery } from './api/membersApi';
import { BulkCoachAssignmentsDialog } from './components/BulkCoachAssignmentsDialog';

// Retain stored definitions and the generic API, but do not offer retired enrollment controls.
const RETIRED_ONBOARDING_FLAGS = new Set([
  'MEMBER_SESSION_ASSETS',
  'MEMBERS_ONLY',
  'CHEM_CALL_REFLECTION_MEMBER',
]);

const TARGET_ROLE_LABELS: Record<TargetRole, string> = {
  coach: 'Coaches',
  member: 'Members',
  all: 'Universal',
};

const FLAG_TYPE_COLORS: Record<FlagType, 'primary' | 'secondary' | 'warning'> = {
  rollout: 'primary',
  experiment: 'secondary',
  ops: 'warning',
};

const ENABLED_FOR_ALL_AUDIENCE: Record<TargetRole, { phrase: string; buttonNoun: string }> = {
  coach: { phrase: 'every coach', buttonNoun: 'coaches' },
  member: { phrase: 'every member', buttonNoun: 'members' },
  all: { phrase: 'everyone (coaches and members)', buttonNoun: 'users' },
};

type PersonOption = {
  id: string;
  label: string;
};

// The target value is captured when the dialog opens so the saved change is
// exactly the one the admin confirmed, even if the list data is stale.
type PendingEnabledForAll = {
  flag: FeatureFlag;
  enabledForAll: boolean;
};

export function FeatureFlagsPage() {
  const [activeTab, setActiveTab] = useState<TargetRole>('coach');
  const [selectedFlag, setSelectedFlag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [pendingEnabledForAll, setPendingEnabledForAll] = useState<PendingEnabledForAll | null>(
    null
  );
  const [newFlag, setNewFlag] = useState<CreateFlagRequest>({
    flag_key: '',
    description: '',
    flag_type: 'rollout',
    target_role: 'coach',
    allow_related_member_reads: false,
    allow_related_coach_reads: false,
  });

  // Queries
  const { data: flagsData, isLoading: flagsLoading } = useListFeatureFlagsQuery({
    targetRole: activeTab,
  });
  const { data: peopleData, isLoading: peopleLoading } = useGetFlagPeopleQuery(
    { flagKey: selectedFlag ?? '' },
    { skip: !selectedFlag }
  );
  const { data: allCoachesData } = useListCoachesQuery(
    {
      q: searchQuery,
      per: 20,
    },
    { skip: activeTab === 'member' }
  );
  const { data: allMembersData } = useListMembersQuery(
    {
      q: searchQuery,
      per: 20,
    },
    { skip: activeTab === 'coach' }
  );

  // Mutations
  const [createFlag, { isLoading: isCreating }] = useCreateFlagMutation();
  const [updateFlag] = useUpdateFeatureFlagMutation();
  const [deleteFlag] = useDeleteFlagMutation();
  const [addPerson] = useAddPersonToFlagMutation();
  const [removePerson] = useRemovePersonFromFlagMutation();

  const handleTabChange = (_: React.SyntheticEvent, newValue: TargetRole) => {
    setActiveTab(newValue);
    setSelectedFlag(null);
    setSelectedPerson(null);
    setBulkDialogOpen(false);
  };

  const handleToggleEnabledForAll = (flag: FeatureFlag) => {
    setPendingEnabledForAll({ flag, enabledForAll: !flag.enabled_for_all });
  };

  const handleConfirmEnabledForAll = async () => {
    if (!pendingEnabledForAll) return;
    const { flag, enabledForAll } = pendingEnabledForAll;
    setPendingEnabledForAll(null);
    await updateFlag({
      flagKey: flag.key,
      enabled_for_all: enabledForAll,
    });
  };

  const handleToggleRelatedMemberReads = async (flag: FeatureFlag) => {
    if (flag.target_role !== 'coach') return;
    await updateFlag({
      flagKey: flag.key,
      allow_related_member_reads: !flag.allow_related_member_reads,
    });
  };

  const handleToggleRelatedCoachReads = async (flag: FeatureFlag) => {
    if (flag.target_role !== 'member') return;
    await updateFlag({
      flagKey: flag.key,
      allow_related_coach_reads: !flag.allow_related_coach_reads,
    });
  };

  const handleAddPerson = async () => {
    if (!selectedFlag || !selectedPerson) return;
    await addPerson({
      flagKey: selectedFlag,
      personId: selectedPerson.id,
    });
    setSelectedPerson(null);
  };

  const handleRemovePerson = async (personId: string) => {
    if (!selectedFlag) return;
    await removePerson({
      flagKey: selectedFlag,
      personId,
    });
  };

  const handleCreateFlag = async () => {
    if (!newFlag.flag_key || RETIRED_ONBOARDING_FLAGS.has(newFlag.flag_key.trim())) return;
    await createFlag(newFlag);
    setCreateDialogOpen(false);
    setNewFlag({
      flag_key: '',
      description: '',
      flag_type: 'rollout',
      target_role: activeTab,
      allow_related_member_reads: false,
      allow_related_coach_reads: false,
    });
  };

  const handleDeleteFlag = async (flagKey: string) => {
    if (!confirm(`Are you sure you want to delete the flag "${flagKey}"? This cannot be undone.`)) {
      return;
    }
    await deleteFlag({ flagKey });
    if (selectedFlag === flagKey) {
      setSelectedFlag(null);
    }
  };

  const visibleCoaches =
    activeTab === 'coach' || activeTab === 'all' ? (allCoachesData?.coaches ?? []) : [];
  const visibleMembers =
    activeTab === 'member' || activeTab === 'all' ? (allMembersData?.members ?? []) : [];

  const personOptionsByID = new Map<string, { name: string; email: string; roles: Set<string> }>();

  for (const coach of visibleCoaches) {
    personOptionsByID.set(coach.id, {
      name: coach.name,
      email: coach.email,
      roles: new Set(['Coach']),
    });
  }

  for (const member of visibleMembers) {
    const existing = personOptionsByID.get(member.id);
    if (existing) {
      existing.roles.add('Member');
      continue;
    }
    personOptionsByID.set(member.id, {
      name: member.name,
      email: member.email,
      roles: new Set(['Member']),
    });
  }

  const personOptions: PersonOption[] = Array.from(personOptionsByID.entries()).map(
    ([id, person]) => {
      const roleLabel = Array.from(person.roles).join(', ');
      return {
        id,
        label: `${person.name} (${person.email})${activeTab === 'all' ? ` - ${roleLabel}` : ''}`,
      };
    }
  );

  const editableFlags = flagsData?.flags.filter((flag) => !RETIRED_ONBOARDING_FLAGS.has(flag.key));
  const selectedFlagData = editableFlags?.find((f) => f.key === selectedFlag);

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Feature Flags</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewFlag((prev) => ({
              ...prev,
              target_role: activeTab,
              allow_related_member_reads: false,
              allow_related_coach_reads: false,
            }));
            setCreateDialogOpen(true);
          }}
        >
          Create Flag
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Coaches" value="coach" />
          <Tab label="Members" value="member" />
          <Tab label="Universal" value="all" />
        </Tabs>
      </Paper>

      {flagsLoading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Flags Table */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {TARGET_ROLE_LABELS[activeTab]} Flags
            </Typography>
            {editableFlags?.length === 0 ? (
              <Alert severity="info">
                No flags for {TARGET_ROLE_LABELS[activeTab].toLowerCase()} yet.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Flag Key</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Enabled All</TableCell>
                    <TableCell>Member Reads</TableCell>
                    <TableCell>Coach Reads</TableCell>
                    <TableCell align="right">People</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editableFlags?.map((flag) => (
                    <TableRow
                      key={flag.key}
                      hover
                      selected={selectedFlag === flag.key}
                      onClick={() => setSelectedFlag(flag.key)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {flag.key}
                          </Typography>
                          {flag.description && (
                            <Typography variant="caption" color="text.secondary">
                              {flag.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={flag.flag_type}
                          size="small"
                          color={FLAG_TYPE_COLORS[flag.flag_type]}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          inputProps={{ 'aria-label': `Enabled for all ${flag.key}` }}
                          checked={flag.enabled_for_all}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleEnabledForAll(flag);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          inputProps={{
                            'aria-label': `Allow related member reads for ${flag.key}`,
                          }}
                          checked={flag.allow_related_member_reads}
                          disabled={flag.target_role !== 'coach'}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleRelatedMemberReads(flag);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          inputProps={{ 'aria-label': `Allow related coach reads for ${flag.key}` }}
                          checked={flag.allow_related_coach_reads}
                          disabled={flag.target_role !== 'member'}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleRelatedCoachReads(flag);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell align="right">{flag.enabled_person_count}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFlag(flag.key);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          {/* Flag Details Panel */}
          {selectedFlagData && (
            <Paper sx={{ width: 400, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedFlag}
              </Typography>

              {peopleLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFlagData?.enabled_for_all ?? false}
                        onChange={() =>
                          selectedFlagData && handleToggleEnabledForAll(selectedFlagData)
                        }
                      />
                    }
                    label={`Enabled all ${TARGET_ROLE_LABELS[activeTab].toLowerCase()}`}
                  />

                  {selectedFlagData && (
                    <Box sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFlagData.allow_related_member_reads}
                            disabled={selectedFlagData.target_role !== 'coach'}
                            onChange={() => handleToggleRelatedMemberReads(selectedFlagData)}
                          />
                        }
                        label="Allow related member reads"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 4 }}
                      >
                        Allows a member with an active relationship to this coach to query this flag
                        for that coach. Use only for coach-anchored rollout flags.
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFlagData.allow_related_coach_reads}
                            disabled={selectedFlagData.target_role !== 'member'}
                            onChange={() => handleToggleRelatedCoachReads(selectedFlagData)}
                          />
                        }
                        label="Allow related coach reads"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 4 }}
                      >
                        Allows a coach with an active relationship to this member to query this flag
                        for that member. Use only for member-anchored rollout flags.
                      </Typography>
                    </Box>
                  )}

                  {peopleData?.enabled_for_all && (
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      This flag is enabled for all {TARGET_ROLE_LABELS[activeTab].toLowerCase()}.
                      Individual assignments below are for tracking purposes.
                    </Alert>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Individual Assignments ({peopleData?.people.length ?? 0})
                  </Typography>

                  {selectedFlagData?.target_role === 'coach' && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setBulkDialogOpen(true)}
                      sx={{ mb: 1 }}
                    >
                      Paste coach emails
                    </Button>
                  )}

                  {peopleData?.people.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No individuals have this flag enabled.
                    </Typography>
                  ) : (
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {peopleData?.people.map((person) => (
                        <ListItem key={person.person_id}>
                          <ListItemText primary={person.name} secondary={person.email} />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemovePerson(person.person_id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      size="small"
                      options={personOptions}
                      value={selectedPerson}
                      onChange={(_e, newValue) => setSelectedPerson(newValue)}
                      onInputChange={(_e, newInputValue) => setSearchQuery(newInputValue)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => <TextField {...params} label="Add person..." />}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddPerson}
                      disabled={!selectedPerson}
                    >
                      Add
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* Create Flag Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Feature Flag</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Flag Key"
              value={newFlag.flag_key}
              onChange={(e) =>
                setNewFlag((prev) => ({ ...prev, flag_key: e.target.value.toUpperCase() }))
              }
              placeholder="e.g., NEW_DASHBOARD"
              error={RETIRED_ONBOARDING_FLAGS.has(newFlag.flag_key.trim())}
              helperText={
                RETIRED_ONBOARDING_FLAGS.has(newFlag.flag_key.trim())
                  ? 'This onboarding rollout flag is retired.'
                  : 'Use UPPER_SNAKE_CASE'
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={newFlag.description}
              onChange={(e) => setNewFlag((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="What does this flag control?"
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="create-flag-type-label">Flag Type</InputLabel>
              <Select
                labelId="create-flag-type-label"
                id="create-flag-type"
                value={newFlag.flag_type}
                label="Flag Type"
                onChange={(e) =>
                  setNewFlag((prev) => ({ ...prev, flag_type: e.target.value as FlagType }))
                }
              >
                <MenuItem value="rollout">Rollout - Gradual feature release</MenuItem>
                <MenuItem value="experiment">Experiment - A/B testing</MenuItem>
                <MenuItem value="ops">Ops - Operational toggle</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="create-flag-target-role-label">Target Audience</InputLabel>
              <Select
                labelId="create-flag-target-role-label"
                id="create-flag-target-role"
                value={newFlag.target_role}
                label="Target Audience"
                onChange={(e) => {
                  const targetRole = e.target.value as TargetRole;
                  setNewFlag((prev) => ({
                    ...prev,
                    target_role: targetRole,
                    allow_related_member_reads:
                      targetRole === 'coach' ? prev.allow_related_member_reads : false,
                    allow_related_coach_reads:
                      targetRole === 'member' ? prev.allow_related_coach_reads : false,
                  }));
                }}
              >
                <MenuItem value="coach">Coaches only</MenuItem>
                <MenuItem value="member">Members only</MenuItem>
                <MenuItem value="all">Universal (all users)</MenuItem>
              </Select>
            </FormControl>
            {newFlag.target_role === 'coach' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newFlag.allow_related_member_reads ?? false}
                    onChange={(e) =>
                      setNewFlag((prev) => ({
                        ...prev,
                        allow_related_member_reads: e.target.checked,
                      }))
                    }
                  />
                }
                label="Allow related member reads"
              />
            )}
            {newFlag.target_role === 'member' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newFlag.allow_related_coach_reads ?? false}
                    onChange={(e) =>
                      setNewFlag((prev) => ({
                        ...prev,
                        allow_related_coach_reads: e.target.checked,
                      }))
                    }
                  />
                }
                label="Allow related coach reads"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFlag}
            variant="contained"
            disabled={
              !newFlag.flag_key ||
              RETIRED_ONBOARDING_FLAGS.has(newFlag.flag_key.trim()) ||
              isCreating
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {pendingEnabledForAll && (
        <EnabledForAllConfirmationDialog
          pending={pendingEnabledForAll}
          onClose={() => setPendingEnabledForAll(null)}
          onConfirm={handleConfirmEnabledForAll}
        />
      )}

      {bulkDialogOpen && selectedFlagData?.target_role === 'coach' && (
        <BulkCoachAssignmentsDialog
          flagKey={selectedFlagData.key}
          open
          onClose={() => setBulkDialogOpen(false)}
        />
      )}
    </Box>
  );
}

function EnabledForAllConfirmationDialog({
  pending,
  onClose,
  onConfirm,
}: {
  pending: PendingEnabledForAll;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { flag, enabledForAll } = pending;
  const audience = ENABLED_FOR_ALL_AUDIENCE[flag.target_role];
  const action = enabledForAll ? 'on' : 'off';
  const flagKey = <strong>{flag.key}</strong>;

  return (
    <ConfirmationDialog
      open
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Turn ${action} ${flag.key} for all?`}
      confirmLabel={`Turn ${action} for all ${audience.buttonNoun}`}
      confirmVariant="warning"
    >
      {enabledForAll ? (
        <>
          <Typography variant="body2">
            This turns {flagKey} on for {audience.phrase}, effective immediately.
          </Typography>
          <Typography variant="body2">
            Today only individually assigned people have it ({flag.enabled_person_count} assigned).
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2">
            This turns {flagKey} off for {audience.phrase} except people individually assigned,
            effective immediately.
          </Typography>
          <Typography variant="body2">
            Anyone who is not individually assigned loses it ({flag.enabled_person_count} assigned).
          </Typography>
        </>
      )}
    </ConfirmationDialog>
  );
}
